import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, CheckCircle, Clock, MoreVertical, Users } from "lucide-react";

export const RosteredAthletesTable = memo(function RosteredAthletesTable({
  athletes,
  playerTypeDraftByAthlete,
  playerTypeByAthlete,
  playerTypeSaving,
  positionSaving,
  playerTypeOptions,
  positionOptions,
  athletePositionIdsByAthlete,
  secondaryPlayerTypeByAthletePosition,
  handleSaveAthletePositions,
  swotStatusByAthlete,
  handlePublishAthleteSwot,
  onPositionsSavedRefresh,
  handleSavePlayerTypeWithValue,
  handleSaveSecondaryPlayerType,
  openActionMenu,
  getAthleteActionKey,
  getAthletePlayerTypeKey,
  getAthletePositionLabel,
  getRosterImportStatusMeta,
  getSwotCtaLabel,
  isSwotCtaDisabled,
  handlePlayerTypeDraftChange,
  handleSavePlayerType,
  handleOpenAthleteSwot,
  handleOpenEditRosterContact,
  handleRemoveAthlete,
  setViewingScores,
  toggleActionMenu
}) {
  const [positionPlayerTypeDrafts, setPositionPlayerTypeDrafts] = useState({});
  const [localPositionIdsByAthlete, setLocalPositionIdsByAthlete] = useState({});
  const refreshTimeoutRef = useRef(null);

  useEffect(() => {
    console.log("[AthleteTables] positionOptions", {
      count: positionOptions?.length || 0,
      sample: (positionOptions || []).slice(0, 10).map((pos) => ({
        id: pos.id,
        name: pos.name,
        abbreviation: pos.abbreviation
      }))
    });
  }, [positionOptions]);

  useEffect(() => {
    setLocalPositionIdsByAthlete((prev) => ({ ...athletePositionIdsByAthlete, ...prev }));
  }, [athletePositionIdsByAthlete]);

  useEffect(() => () => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  const handlePositionPlayerTypeDraftChange = (athleteKey, positionId, value) => {
    if (!athleteKey || !positionId) return;
    setPositionPlayerTypeDrafts((prev) => ({
      ...prev,
      [athleteKey]: {
        ...(prev[athleteKey] || {}),
        [positionId]: value
      }
    }));
  };

  const handlePositionsSaved = (athleteKey, positionIds) => {
    if (!athleteKey) return;
    setLocalPositionIdsByAthlete((prev) => ({
      ...prev,
      [athleteKey]: positionIds
    }));
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = window.setTimeout(() => {
      onPositionsSavedRefresh?.();
    }, 150);
  };

  const savePlayerTypeValue = (athlete, playerTypeId) => {
    if (handleSavePlayerTypeWithValue) {
      handleSavePlayerTypeWithValue(athlete, playerTypeId || "");
      return;
    }
    handlePlayerTypeDraftChange(athlete, playerTypeId || "");
    handleSavePlayerType(athlete);
  };
  if (athletes.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
        <Users className="mx-auto text-gray-300 mb-3" size={40} />
        <p className="text-gray-500 font-medium">No athletes found in this roster.</p>
      </div>
    );
  }
  const sortedAthletes = [...athletes].sort((a, b) => {
    const nameA = String(a?.users?.full_name || a?.email || "").trim();
    const nameB = String(b?.users?.full_name || b?.email || "").trim();
    const firstA = (nameA.split(/\s+/)[0] || "").toLowerCase();
    const firstB = (nameB.split(/\s+/)[0] || "").toLowerCase();
    if (firstA === firstB) return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
    return firstA.localeCompare(firstB);
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-visible">
      <div className="hidden md:grid grid-cols-12 bg-gray-50/50 border-b border-gray-100 px-6 py-4">
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Pos</div>
        <div className="col-span-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Athlete</div>
        <div className="col-span-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Player Type</div>
        <div className="col-span-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Analysis</div>
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Skill</div>
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Combine</div>
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Overall</div>
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Action</div>
      </div>

      <div className="divide-y divide-gray-50">
        {sortedAthletes.map((athlete) => {
          const athleteKey = getAthletePlayerTypeKey(athlete);
          const basePlayerTypeValue = playerTypeDraftByAthlete[athleteKey]
            ?? (playerTypeByAthlete[athleteKey]?.player_type || "")
            ?? (athlete?.matchedDraft?.player_type || "");
          const basePositionIds = localPositionIdsByAthlete?.[athleteKey] || athletePositionIdsByAthlete?.[athleteKey] || [];
          const draftPlayerTypes = {
            ...(secondaryPlayerTypeByAthletePosition?.[athleteKey] || {}),
            ...(positionPlayerTypeDrafts[athleteKey] || {})
          };
          const swotStatus = swotStatusByAthlete?.[athleteKey];

          return (
            <RosteredAthleteRow
              key={getAthleteActionKey(athlete)}
              athlete={athlete}
              basePlayerTypeValue={basePlayerTypeValue}
              basePositionIds={basePositionIds}
              draftPlayerTypes={draftPlayerTypes}
              onUpdatePositionDraft={(positionId, value) =>
                handlePositionPlayerTypeDraftChange(athleteKey, positionId, value)
              }
              swotStatus={swotStatus}
              onPublishSwot={handlePublishAthleteSwot}
              playerTypeOptions={playerTypeOptions}
              positionOptions={positionOptions}
              getAthletePositionLabel={getAthletePositionLabel}
              getRosterImportStatusMeta={getRosterImportStatusMeta}
              getSwotCtaLabel={getSwotCtaLabel}
              isSwotCtaDisabled={isSwotCtaDisabled}
              handlePlayerTypeDraftChange={handlePlayerTypeDraftChange}
              savePlayerTypeValue={savePlayerTypeValue}
              handleOpenAthleteSwot={handleOpenAthleteSwot}
              handleOpenEditRosterContact={handleOpenEditRosterContact}
              handleRemoveAthlete={handleRemoveAthlete}
              setViewingScores={setViewingScores}
              toggleActionMenu={toggleActionMenu}
              openActionMenu={openActionMenu}
              getAthleteActionKey={getAthleteActionKey}
              isSaving={Boolean(playerTypeSaving[athleteKey] || positionSaving?.[athleteKey])}
              onSavePositions={handleSaveAthletePositions}
              onPositionsSaved={(positionIds) => handlePositionsSaved(athleteKey, positionIds)}
              handleSaveSecondaryPlayerType={handleSaveSecondaryPlayerType}
            />
          );
        })}
      </div>
    </div>
  );
});

const buildPositionLabel = (position) => {
  if (!position) return "--";
  return String(position.abbreviation || position.name || "--").trim() || "--";
};

const buildPlayerTypeName = (playerTypeOptions, value) => {
  if (!value) return "Not set";
  const match = playerTypeOptions.find((option) => String(option.id) === String(value));
  return match?.name || "Not set";
};

const normalizeFallbackPositions = (label, positionOptions) => {
  const parts = String(label || "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return [];
  const matches = [];
  parts.forEach((part) => {
    const normalized = part.toUpperCase();
    const match = positionOptions.find((option) => {
      const abbr = String(option.abbreviation || "").toUpperCase();
      const name = String(option.name || "").toUpperCase();
      return normalized === abbr || normalized === name || normalized === name.replace(/\s+/g, "");
    });
    if (match && !matches.includes(String(match.id))) {
      matches.push(String(match.id));
    }
  });
  return matches;
};

const buildRosterPositions = ({
  basePositionIds,
  fallbackLabel,
  positionOptions,
  basePlayerTypeValue,
  draftPlayerTypes
}) => {
  const baseIds = basePositionIds?.length
    ? basePositionIds.map((id) => String(id))
    : normalizeFallbackPositions(fallbackLabel, positionOptions);
  const ids = baseIds.slice(0, 2);
  return ids.map((id, index) => ({
    positionId: id,
    playerTypeId: draftPlayerTypes?.[id] ?? (index === 0 ? String(basePlayerTypeValue || "") : ""),
    isPrimary: index === 0
  }));
};

const positionsEqual = (left, right) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (
      left[i].positionId !== right[i].positionId ||
      left[i].playerTypeId !== right[i].playerTypeId ||
      left[i].isPrimary !== right[i].isPrimary
    ) {
      return false;
    }
  }
  return true;
};

const positionsStructureEqual = (left, right) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (left[i].positionId !== right[i].positionId || left[i].isPrimary !== right[i].isPrimary) {
      return false;
    }
  }
  return true;
};

const normalizePositions = (positions) => {
  const primary = positions.find((pos) => pos.isPrimary) || positions[0];
  const secondary = positions.find((pos) => !pos.isPrimary && pos.positionId !== primary?.positionId);
  const normalized = [];
  if (primary?.positionId) {
    normalized.push({ ...primary, isPrimary: true });
  }
  if (secondary?.positionId) {
    normalized.push({ ...secondary, isPrimary: false });
  }
  return normalized.slice(0, 2);
};

const RosteredAthleteRow = ({
  athlete,
  basePlayerTypeValue,
  basePositionIds,
  draftPlayerTypes,
  onUpdatePositionDraft,
  playerTypeOptions,
  positionOptions,
  onSavePositions,
  swotStatus,
  onPublishSwot,
  onPositionsSaved,
  handleSaveSecondaryPlayerType,
  getAthletePositionLabel,
  getRosterImportStatusMeta,
  getSwotCtaLabel,
  isSwotCtaDisabled,
  handlePlayerTypeDraftChange,
  savePlayerTypeValue,
  handleOpenAthleteSwot,
  handleOpenEditRosterContact,
  handleRemoveAthlete,
  setViewingScores,
  toggleActionMenu,
  openActionMenu,
  getAthleteActionKey,
  isSaving
}) => {
  const [expanded, setExpanded] = useState(false);
  const [positionSaveState, setPositionSaveState] = useState("idle");
  const [secondarySaveState, setSecondarySaveState] = useState("idle");
  const [applySamePlayerType, setApplySamePlayerType] = useState(false);

  const initialPositions = useMemo(
    () => buildRosterPositions({
      basePositionIds,
      fallbackLabel: getAthletePositionLabel(athlete),
      positionOptions,
      basePlayerTypeValue,
      draftPlayerTypes
    }),
    [athlete, basePlayerTypeValue, basePositionIds, draftPlayerTypes, getAthletePositionLabel, positionOptions]
  );
  const basePositionsKey = useMemo(
    () => (basePositionIds || []).map((id) => String(id)).join("|"),
    [basePositionIds]
  );

  const [editPositions, setEditPositions] = useState(() => normalizePositions(initialPositions));
  const [savedPositions, setSavedPositions] = useState(() => normalizePositions(initialPositions));

  const positionById = useMemo(() => {
    const map = new Map();
    (positionOptions || []).forEach((option) => {
      map.set(String(option.id), option);
    });
    return map;
  }, [positionOptions]);

  const selectedPositionIds = editPositions.map((pos) => pos.positionId);
  const summary = (() => {
    const labels = editPositions.map((pos) => buildPositionLabel(positionById.get(pos.positionId)));
    const playerTypeIds = editPositions.map((pos) => pos.playerTypeId).filter(Boolean);
    const uniqueTypes = Array.from(new Set(playerTypeIds.map((id) => String(id))));
    if (labels.length > 1 && uniqueTypes.length === 1 && uniqueTypes[0]) {
      return `${labels.join("/")}: ${buildPlayerTypeName(playerTypeOptions, uniqueTypes[0])}`;
    }
    return editPositions
      .map((pos) => {
        const label = buildPositionLabel(positionById.get(pos.positionId));
        return `${label}: ${buildPlayerTypeName(playerTypeOptions, pos.playerTypeId)}`;
      })
      .join(" | ");
  })();

  const dirtyPositions = !positionsStructureEqual(editPositions, savedPositions);
  const dirtyPlayerTypes = !positionsEqual(editPositions, savedPositions) && !dirtyPositions;
  const dirty = dirtyPlayerTypes;
  const valid = editPositions.length > 0 && editPositions.length <= 2;
  const canExpand = editPositions.length > 1;
  const canSavePlayerTypes = dirtyPlayerTypes && valid && !isSaving;

  const commitPlayerTypes = useCallback(() => {
    if (!dirtyPlayerTypes || !valid || isSaving) return;
    const basePrimaryId = (basePositionIds || [])[0] ? String((basePositionIds || [])[0]) : "";
    const baseSecondaryId = (basePositionIds || [])[1] ? String((basePositionIds || [])[1]) : "";
    const primary = basePrimaryId
      ? (editPositions.find((pos) => pos.positionId === basePrimaryId) || editPositions[0])
      : (editPositions.find((pos) => pos.isPrimary) || editPositions[0]);
    const secondary = baseSecondaryId
      ? editPositions.find((pos) => pos.positionId === baseSecondaryId)
      : editPositions.find((pos) => pos.positionId !== primary?.positionId);
    editPositions.forEach((pos) => {
      onUpdatePositionDraft?.(pos.positionId, pos.playerTypeId || "");
    });
    handlePlayerTypeDraftChange(athlete, primary?.playerTypeId || "");
    savePlayerTypeValue?.(athlete, primary?.playerTypeId || "");
    if (secondary?.positionId) {
      setSecondarySaveState("saving");
      Promise.resolve(
        handleSaveSecondaryPlayerType?.(athlete, secondary.positionId, secondary.playerTypeId || "")
      )
        .then(() => {
          setSecondarySaveState("saved");
          window.setTimeout(() => setSecondarySaveState("idle"), 2000);
        })
        .catch(() => {
          setSecondarySaveState("idle");
        });
    } else {
      setSecondarySaveState("idle");
    }
    setSavedPositions(editPositions);
  }, [
    dirtyPlayerTypes,
    editPositions,
    handlePlayerTypeDraftChange,
    handleSaveSecondaryPlayerType,
    isSaving,
    onUpdatePositionDraft,
    savePlayerTypeValue,
    valid
  ]);

  // Manual save only for player types (no autosave).

  useEffect(() => {
    if (dirty) return;
    const nextPositions = normalizePositions(initialPositions);
    setEditPositions(nextPositions);
    setSavedPositions(nextPositions);
  }, [basePositionsKey, basePlayerTypeValue, athlete.id, dirty, initialPositions]);

  const handlePlayerTypeChange = (positionId, value) => {
    setEditPositions((prev) => {
      if (!applySamePlayerType) {
        return prev.map((pos) =>
          pos.positionId === positionId ? { ...pos, playerTypeId: value } : pos
        );
      }
      return prev.map((pos) => ({ ...pos, playerTypeId: value }));
    });
    onUpdatePositionDraft?.(positionId, value);
    if (applySamePlayerType) {
      editPositions
        .filter((pos) => pos.positionId !== positionId)
        .forEach((pos) => {
          onUpdatePositionDraft?.(pos.positionId, value);
        });
    }
  };

  const handlePositionsChange = (nextPositions) => {
    const next = nextPositions.slice(0, 2).map((id, index) => {
      const existing = editPositions.find((pos) => pos.positionId === id);
      const nextPlayerTypeId = existing?.playerTypeId || draftPlayerTypes?.[id] || "";
      return {
        positionId: id,
        playerTypeId: nextPlayerTypeId,
        isPrimary: index === 0
      };
    });
    setEditPositions(normalizePositions(next));
  };

  const handleSave = () => {
    commitPlayerTypes();
  };

  const handleSavePositions = async () => {
    const primary = editPositions.find((pos) => pos.isPrimary) || editPositions[0];
    const secondary = editPositions.find((pos) => !pos.isPrimary);
    setPositionSaveState("saving");
    try {
      await onSavePositions?.(athlete, primary?.positionId || null, secondary?.positionId || null);
      onPositionsSaved?.([primary?.positionId || null, secondary?.positionId || null].filter(Boolean));
      setPositionSaveState("saved");
      window.setTimeout(() => setPositionSaveState("idle"), 2000);
    } catch (err) {
      setPositionSaveState("idle");
    }
  };

  return (
    <div className="group">
      <div className="grid grid-cols-1 md:grid-cols-12 items-center px-4 md:px-6 py-5 hover:bg-blue-50/30 transition-all">
        <div className="hidden md:flex col-span-1 pr-3">
          <div className="flex w-full flex-col gap-2">
            <MultiSelectPositions
              selected={selectedPositionIds}
              onChange={handlePositionsChange}
              options={positionOptions}
              disabled={athlete.type === "pending"}
              compact
              showChips
              combineChips
              showCount={false}
              buttonClassName="justify-start"
              menuClassName="w-44"
              showSave
              onSave={handleSavePositions}
              saveDisabled={!valid || positionSaveState === "saving"}
              saveState={positionSaveState}
            />
          </div>
        </div>

        <div className="hidden md:flex col-span-2 flex-col pr-4">
          <span className="font-bold text-gray-900 text-[15px] leading-tight mb-0.5">
            {athlete.users?.full_name || athlete.email || "Unnamed Athlete"}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {athlete.type !== "pending" && getRosterImportStatusMeta(athlete.importStatus) ? (
              <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${getRosterImportStatusMeta(athlete.importStatus).className}`}>
                {getRosterImportStatusMeta(athlete.importStatus).label}
              </span>
            ) : null}
            {athlete.type !== "pending" && athlete.user_id && athlete.users?.email && athlete.matchedDraft ? (
              <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                Match Found
              </span>
            ) : null}
          </div>
        </div>

        <div className="hidden md:flex col-span-3 pr-4 items-start gap-2">
          <div className="w-full min-w-0 space-y-2">
            {editPositions.length === 1 ? (
              <div className="flex items-center gap-2">
                <label className="sr-only">Player Type</label>
                <div className="relative w-full min-w-0">
                  <select
                    className="w-full bg-white border border-gray-200 text-gray-900 text-xs rounded-lg py-2 pl-2 pr-8 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 appearance-none"
                    value={editPositions[0].playerTypeId || ""}
                    onChange={(e) => handlePlayerTypeChange(editPositions[0].positionId, e.target.value)}
                    disabled={athlete.type === "pending" || isSaving || playerTypeOptions.length === 0}
                  >
                    <option value="">
                      {playerTypeOptions.length === 0 ? "No player types" : "Select player type"}
                    </option>
                    {playerTypeOptions.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" size={12} />
                </div>
                <button
                  onClick={handleSave}
                  disabled={!dirty || !valid || isSaving}
                  className="w-[64px] px-2.5 py-2 text-[10px] font-bold uppercase rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-blue-600 shrink-0"
                >
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-gray-600 font-semibold">{summary || "Not set"}</div>
                {canExpand ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => !prev)}
                    className="flex items-center gap-1 text-[10px] font-bold uppercase text-gray-500 hover:text-gray-700"
                  >
                    {expanded ? "Collapse" : "Expand"}
                    <ChevronDown className={`transition-transform ${expanded ? "rotate-180" : ""}`} size={12} />
                  </button>
                ) : null}
              </div>
            )}

            {!valid && (
              <div className="text-[10px] font-semibold text-rose-500">Select at least 1 position</div>
            )}
          </div>
        </div>

        <div className="md:hidden flex items-start justify-between gap-2">
          <div className="min-w-0 pr-2 space-y-2">
            <div className="flex flex-col gap-2">
              <MultiSelectPositions
                selected={selectedPositionIds}
                onChange={handlePositionsChange}
                options={positionOptions}
                disabled={athlete.type === "pending"}
                compact
                showChips
                combineChips
                showCount={false}
                buttonClassName="justify-start"
                menuClassName="w-44"
                showSave
                onSave={handleSavePositions}
                saveDisabled={!valid || positionSaveState === "saving"}
                saveState={positionSaveState}
              />
            </div>
            <span className="block font-bold text-gray-900 text-sm truncate">
              {athlete.users?.full_name || athlete.email || "Unnamed Athlete"}
            </span>
            {athlete.type !== "pending" && getRosterImportStatusMeta(athlete.importStatus) ? (
              <span className={`inline-flex text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${getRosterImportStatusMeta(athlete.importStatus).className}`}>
                {getRosterImportStatusMeta(athlete.importStatus).label}
              </span>
            ) : null}

            {editPositions.length === 1 ? (
              <div className="flex items-center gap-2">
                <label className="sr-only">Player Type</label>
                <div className="relative min-w-[150px]">
                  <select
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-[11px] rounded-md py-1 pl-2 pr-7 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 appearance-none"
                    value={editPositions[0].playerTypeId || ""}
                    onChange={(e) => handlePlayerTypeChange(editPositions[0].positionId, e.target.value)}
                    disabled={athlete.type === "pending" || isSaving || playerTypeOptions.length === 0}
                  >
                    <option value="">
                      {playerTypeOptions.length === 0 ? "No player types" : "Select player type"}
                    </option>
                    {playerTypeOptions.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1.5 text-gray-400 pointer-events-none" size={12} />
                </div>
                <button
                  onClick={handleSave}
                  disabled={!dirty || !valid || isSaving}
                  className="w-[56px] px-2 py-1 text-[10px] font-bold uppercase rounded-md border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-blue-600 shrink-0"
                >
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-semibold text-gray-600">{summary || "Not set"}</div>
                {canExpand ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => !prev)}
                    className="flex items-center gap-1 text-[9px] font-bold uppercase text-gray-500 hover:text-gray-700"
                  >
                    {expanded ? "Collapse" : "Expand"}
                    <ChevronDown className={`transition-transform ${expanded ? "rotate-180" : ""}`} size={12} />
                  </button>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => handleOpenAthleteSwot(athlete)}
              disabled={athlete.type === "pending" || isSwotCtaDisabled(athlete)}
              className={`w-[152px] px-2 py-1.5 text-[11px] font-bold uppercase rounded-lg border transition-all ${(athlete.type === "pending" || isSwotCtaDisabled(athlete))
                ? "bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed"
                : "bg-white text-blue-600 border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600"
                }`}
            >
              {getSwotCtaLabel(athlete)}
            </button>
            <button
              onClick={() => athlete.type !== "pending" && setViewingScores({
                type: "overall",
                athleteName: athlete.users?.full_name,
                overall: athlete.overall,
                skillData: athlete.user_skill_scores || [],
                combineData: athlete.user_combine_scores || []
              })}
              className={`px-2 py-1.5 text-[11px] font-extrabold rounded-lg border ${athlete.type === "pending" ? "bg-gray-50 text-gray-300 border-gray-200" : "bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:text-blue-600"}`}
            >
              {athlete.type === "pending" ? "--" : `${athlete.overall}%`}
            </button>
            <div className="relative" data-action-menu="true">
              <button
                onClick={() => toggleActionMenu(getAthleteActionKey(athlete))}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all border border-gray-200 bg-white"
                aria-label="Open actions"
                data-action-menu="true"
              >
                <MoreVertical size={14} />
              </button>
              {openActionMenu === getAthleteActionKey(athlete) ? (
                <div className="absolute right-0 bottom-full mb-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-action-menu="true">
                  <button
                    onClick={() => handleOpenEditRosterContact(athlete)}
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Edit Contact Info
                  </button>
                  <button
                    onClick={() => {
                      toggleActionMenu(null);
                      window.location.href = `mailto:${athlete.email}`;
                    }}
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Contact
                  </button>
                  <button
                    onClick={() => handleRemoveAthlete(athlete)}
                    className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                  {swotStatus?.status === "draft" ? (
                    <button
                      onClick={() => onPublishSwot?.(athlete)}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-600 hover:bg-emerald-50"
                    >
                      Publish
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="hidden md:flex col-span-2 justify-center py-2 md:py-0">
          {athlete.type !== "pending" ? (
            <button
              onClick={() => handleOpenAthleteSwot(athlete)}
              disabled={isSwotCtaDisabled(athlete)}
              className={`w-full max-w-[152px] min-w-[152px] text-[10px] font-bold uppercase py-1.5 rounded-full shadow-sm transition-all active:scale-95 ${isSwotCtaDisabled(athlete)
                ? "bg-gray-50 text-gray-300 border border-gray-200 cursor-not-allowed"
                : "bg-white border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600"
                }`}
            >
              {getSwotCtaLabel(athlete)}
            </button>
          ) : (
            <span className="text-[10px] font-medium text-gray-300 italic">No Data</span>
          )}
        </div>

        <div className="hidden md:block col-span-1 text-center">
          <button
            onClick={() => athlete.type !== "pending" && setViewingScores({ type: "skill", data: athlete.user_skill_scores, athleteName: athlete.users?.full_name })}
            className={`text-sm font-extrabold ${athlete.type === "pending" ? "text-gray-200" : "text-gray-700 hover:text-blue-600"}`}
          >
            {athlete.type === "pending" ? "--" : athlete.skillAvg}
          </button>
        </div>

        <div className="hidden md:block col-span-1 text-center">
          <button
            onClick={() => athlete.type !== "pending" && setViewingScores({ type: "combine", data: athlete.user_combine_scores, athleteName: athlete.users?.full_name })}
            className={`text-sm font-extrabold ${athlete.type === "pending" ? "text-gray-200" : "text-gray-700 hover:text-blue-600"}`}
          >
            {athlete.type === "pending" ? "--" : athlete.combineAvg}
          </button>
        </div>

        <div className="hidden md:flex col-span-1 flex-col items-center px-2">
          {athlete.type === "pending" ? (
            <span className="text-[10px] text-gray-300 font-medium">Awaiting Data</span>
          ) : (
            <span className="text-sm font-black text-blue-600">{athlete.overall}%</span>
          )}
        </div>

        <div className="hidden md:flex col-span-1 justify-end">
          <div className="relative" data-action-menu="true">
            <button
              onClick={() => toggleActionMenu(getAthleteActionKey(athlete))}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
              aria-label="Open actions"
              data-action-menu="true"
            >
              <MoreVertical size={18} />
            </button>
            {openActionMenu === getAthleteActionKey(athlete) ? (
              <div className="absolute right-0 bottom-full mb-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10" data-action-menu="true">
                <button
                  onClick={() => handleOpenEditRosterContact(athlete)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Edit Contact Info
                </button>
                <button
                  onClick={() => {
                    toggleActionMenu(null);
                    window.location.href = `mailto:${athlete.email}`;
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Contact
                </button>
                <button
                  onClick={() => handleRemoveAthlete(athlete)}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
                {swotStatus?.status === "draft" ? (
                  <button
                    onClick={() => onPublishSwot?.(athlete)}
                    className="w-full text-left px-3 py-2 text-sm font-semibold text-emerald-600 hover:bg-emerald-50"
                  >
                    Publish
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {canExpand ? (
        <ExpandablePositionEditor
          expanded={expanded}
          positions={editPositions}
          positionById={positionById}
          playerTypeOptions={playerTypeOptions}
          onChange={handlePlayerTypeChange}
          onSave={handleSave}
          dirty={dirtyPlayerTypes}
          valid={valid}
          isSaving={isSaving}
          applySamePlayerType={applySamePlayerType}
          onToggleApplySame={(checked) => {
            setApplySamePlayerType(checked);
            if (checked) {
              const primary = editPositions.find((pos) => pos.isPrimary) || editPositions[0];
              if (primary?.playerTypeId) {
                const nextValue = primary.playerTypeId;
                setEditPositions((prev) => prev.map((pos) => ({ ...pos, playerTypeId: nextValue })));
                editPositions.forEach((pos) => {
                  onUpdatePositionDraft?.(pos.positionId, nextValue);
                });
              }
            }
          }}
          secondarySaveState={secondarySaveState}
        />
      ) : null}
    </div>
  );
};

const ExpandablePositionEditor = ({
  expanded,
  positions,
  positionById,
  playerTypeOptions,
  onChange,
  onSave,
  dirty,
  valid,
  isSaving,
  applySamePlayerType,
  onToggleApplySame
  ,
  secondarySaveState
}) => {
  return (
    <div className={`${expanded ? "border-t border-gray-100 bg-gray-50/40 px-4 md:px-6" : "border-t border-transparent"}`}>
      <div className={`transition-all duration-300 ${expanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}>
        <div className={`flex items-center justify-between ${expanded ? "py-3" : "py-0"}`}>
          <div className="flex items-center gap-3">
            <p className="text-[11px] font-bold uppercase text-gray-500">Edit Positions</p>
            {positions.length > 1 ? (
              <label className="flex items-center gap-2 text-[10px] font-semibold text-gray-500">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                  checked={applySamePlayerType}
                  onChange={(e) => onToggleApplySame?.(e.target.checked)}
                  disabled={isSaving}
                />
                Apply same player type
              </label>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || !valid || isSaving}
            className="px-3 py-1.5 text-[10px] font-bold uppercase rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-blue-600"
          >
            Save All
          </button>
        </div>
        <div className="grid gap-3 pb-4">
          {positions.map((pos) => {
            const label = buildPositionLabel(positionById.get(pos.positionId));
            const showSecondarySaved = !pos.isPrimary && secondarySaveState === "saved";
            return (
              <div key={pos.positionId} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase text-gray-600">
                    {pos.isPrimary ? `Primary (${label})` : `Secondary (${label})`}
                  </span>
                  {!pos.playerTypeId ? (
                    <span className="text-[10px] font-semibold text-amber-600">Not set</span>
                  ) : showSecondarySaved ? (
                    <span className="text-[10px] font-semibold text-emerald-600">Saved</span>
                  ) : null}
                </div>
              <div className="mt-2 relative">
                <select
                  className="w-full bg-white border border-gray-200 text-gray-900 text-xs rounded-lg py-2 pl-2 pr-8 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 appearance-none"
                  value={pos.playerTypeId || ""}
                  onChange={(e) => onChange(pos.positionId, e.target.value)}
                  disabled={isSaving || playerTypeOptions.length === 0}
                >
                  <option value="">
                    {playerTypeOptions.length === 0 ? "No player types" : "Select player type"}
                  </option>
                  {playerTypeOptions.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" size={12} />
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

const MultiSelectPositions = ({
  selected,
  onChange,
  options,
  disabled,
  compact,
  showChips = true,
  combineChips = false,
  buttonLabel = "Edit Positions",
  buttonClassName = "",
  menuClassName = "",
  showCount = true,
  showSave = false,
  onSave,
  saveDisabled = false,
  saveState = "idle"
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutside = (event) => {
      if (!containerRef.current || !(event.target instanceof Node)) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((id) => id !== value));
      return;
    }
    if (selected.length >= 2) return;
    onChange([...selected, value]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
        className={`inline-flex min-w-[88px] flex-wrap items-center justify-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-100 disabled:opacity-60 ${compact ? "min-h-[38px]" : "min-h-[42px]"} ${buttonClassName}`}
      >
        {showChips ? (
          selected.length === 0 ? (
            <span className="text-gray-400">Select positions</span>
          ) : combineChips && selected.length > 1 ? (
            <span className="text-[11px] font-black uppercase tracking-wide text-gray-700">
              {selected
                .map((id) => buildPositionLabel(options.find((item) => String(item.id) === String(id))))
                .filter(Boolean)
                .join("/")}
            </span>
          ) : (
            selected.map((id) => {
              const option = options.find((item) => String(item.id) === String(id));
              return (
                <span
                  key={id}
                  className="text-[11px] font-black uppercase tracking-wide text-gray-700"
                >
                  {buildPositionLabel(option)}
                </span>
              );
            })
          )
        ) : (
          <span className="text-[10px] font-bold uppercase text-gray-500">{buttonLabel}</span>
        )}
        {showCount ? <span className="ml-auto text-[10px] text-gray-400">{selected.length}/2</span> : null}
      </button>
      {open ? (
        <div className={`absolute z-10 mt-2 rounded-lg border border-gray-200 bg-white shadow-lg ${menuClassName || "w-full"}`}>
          <div className="max-h-72 overflow-auto p-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200">
            {options.map((option) => {
              const id = String(option.id);
              const checked = selected.includes(id);
              const isDisabled = !checked && selected.length >= 2;
              const positionIndex = selected.findIndex((value) => String(value) === id);
              const tagLabel = positionIndex === 0 ? "Primary" : positionIndex === 1 ? "Secondary" : "";
              return (
                <label
                  key={id}
                  className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-[11px] font-semibold ${isDisabled ? "cursor-not-allowed text-gray-300" : "text-gray-700 hover:bg-gray-50"}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    checked={checked}
                    disabled={isDisabled}
                    onChange={() => toggleOption(id)}
                  />
                  <span>{buildPositionLabel(option)}</span>
                  {tagLabel ? (
                    <span className={`ml-auto rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${tagLabel === "Primary" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>
                      {tagLabel}
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
          <div className="border-t border-gray-100 px-3 py-2 text-[10px] text-gray-500">
            First selection is primary.
          </div>
          {showSave ? (
            <div className="border-t border-gray-100 px-3 py-2">
              <button
                type="button"
                onClick={() => onSave?.()}
                disabled={saveDisabled}
                className={`w-full rounded-md border px-2 py-1.5 text-[10px] font-bold uppercase transition ${saveState === "saved"
                  ? "border-emerald-600 bg-emerald-600 text-white"
                  : "border-emerald-200 text-emerald-600 hover:bg-emerald-600 hover:text-white hover:border-emerald-600"
                  } disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-emerald-600`}
              >
                {saveState === "saving" ? "Saving..." : saveState === "saved" ? "Saved" : "Save Positions"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const DraftedAthletesTable = memo(function DraftedAthletesTable({
  draftEvaluations,
  rosteredByEmail,
  playerTypeDraftByAthlete,
  playerTypeByAthlete,
  playerTypeSaving,
  playerTypeOptions,
  getAthletePlayerTypeKey,
  getAthletePositionLabel,
  handlePlayerTypeDraftChange,
  handleSaveDraftPlayerType,
  handleOpenDraftSwot,
  handlePublishPrebuiltSwot
}) {
  if (draftEvaluations.length === 0) {
    return (
      <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
        <Users className="mx-auto text-gray-300 mb-3" size={40} />
        <p className="text-gray-500 font-medium">No drafted athletes yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="hidden md:grid grid-cols-12 bg-gray-50/50 border-b border-gray-100 px-6 py-4">
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</div>
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Pos</div>
        <div className="col-span-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Athlete</div>
        <div className="col-span-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Player Type</div>
        <div className="col-span-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Analysis</div>
        <div className="col-span-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email</div>
        <div className="col-span-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Action</div>
      </div>
      <div className="divide-y divide-gray-50">
        {draftEvaluations.map((draft) => {
          const matchedAthlete = rosteredByEmail.get((draft.prebuilt_email || "").trim().toLowerCase());
          const isPublishedDraft = draft.status === "published";
          const draftKey = getAthletePlayerTypeKey(draft);
          const selectedDraftPlayerType =
            playerTypeDraftByAthlete[draftKey] ??
            draft.player_type ??
            playerTypeByAthlete[draftKey]?.player_type ??
            "";

          return (
            <div
              key={draft.id}
              className="grid grid-cols-1 md:grid-cols-12 items-start md:items-center gap-4 md:gap-0 px-4 md:px-6 py-5 hover:bg-blue-50/30 transition-all"
            >
              <div className="md:hidden flex flex-col gap-3">
                <div className="min-w-0">
                  <div className="mb-1">
                    <span className="inline-flex min-w-[42px] justify-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-gray-600">
                      {getAthletePositionLabel(draft) || "--"}
                    </span>
                  </div>
                  <span className="block font-bold text-gray-900 text-sm truncate">
                    {draft.prebuilt_name || "Unnamed Draft"}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">
                    {draft.prebuilt_email || "No email"}
                  </span>
                  <div className="mt-2 flex gap-2">
                    <div className="relative min-w-0 flex-1">
                      <select
                        className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-[11px] rounded-md py-1 pl-2 pr-7 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 appearance-none"
                        value={selectedDraftPlayerType}
                        onChange={(e) => handlePlayerTypeDraftChange(draft, e.target.value)}
                        disabled={playerTypeSaving[draftKey] || playerTypeOptions.length === 0}
                      >
                        <option value="">
                          {playerTypeOptions.length === 0 ? "No player types" : "Select player type"}
                        </option>
                        {playerTypeOptions.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1.5 text-gray-400 pointer-events-none" size={12} />
                    </div>
                    <button
                      onClick={() => handleSaveDraftPlayerType(draft)}
                      disabled={playerTypeSaving[draftKey] || !selectedDraftPlayerType}
                      className="w-[56px] px-2 py-1 text-[10px] font-bold uppercase rounded-md border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-blue-600 shrink-0"
                    >
                      Save
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => handleOpenDraftSwot(draft)}
                    className="px-2 py-1.5 text-[11px] font-bold uppercase rounded-lg border bg-white text-blue-600 border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                  >
                    SWOT
                  </button>
                  <div className="ml-auto flex items-center">
                    {isPublishedDraft ? (
                      <span className="text-[10px] font-bold uppercase bg-green-100 text-green-700 px-2 py-1 rounded">
                        Ready
                      </span>
                    ) : (
                      <button
                        onClick={() => handlePublishPrebuiltSwot(draft.id)}
                        className="px-2 py-1.5 text-[10px] font-bold uppercase rounded-lg bg-green-600 text-white hover:bg-green-700 transition-all"
                      >
                        Publish
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="hidden md:flex col-span-1 mb-2 md:mb-0">
                {isPublishedDraft ? (
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50">
                    <CheckCircle size={16} className="text-green-600" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-50">
                    <Clock size={16} className="text-yellow-600" />
                  </div>
                )}
              </div>
              <div className="hidden md:flex col-span-1 pr-3">
                <span className="inline-flex min-w-[42px] justify-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-gray-600">
                  {getAthletePositionLabel(draft) || "--"}
                </span>
              </div>
              <div className="hidden md:flex col-span-2 flex-col pr-4">
                <span className="font-bold text-gray-900 text-[15px] leading-tight mb-0.5">
                  {draft.prebuilt_name || "Unnamed Draft"}
                </span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${isPublishedDraft ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {isPublishedDraft ? "Published" : "Draft"}
                  </span>
                  {matchedAthlete ? (
                    <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Match Found</span>
                  ) : null}
                </div>
              </div>
              <div className="hidden md:flex col-span-3 pr-4 items-center gap-2">
                <div className="relative w-full min-w-0">
                  <select
                    className="w-full bg-white border border-gray-200 text-gray-900 text-xs rounded-lg py-2 pl-2 pr-8 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 appearance-none"
                    value={selectedDraftPlayerType}
                    onChange={(e) => handlePlayerTypeDraftChange(draft, e.target.value)}
                    disabled={playerTypeSaving[draftKey] || playerTypeOptions.length === 0}
                  >
                    <option value="">
                      {playerTypeOptions.length === 0 ? "No player types" : "Select player type"}
                    </option>
                    {playerTypeOptions.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" size={12} />
                </div>
                <button
                  onClick={() => handleSaveDraftPlayerType(draft)}
                  disabled={playerTypeSaving[draftKey] || !selectedDraftPlayerType}
                  className="w-[64px] px-2.5 py-2 text-[10px] font-bold uppercase rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-blue-600 shrink-0"
                >
                  Save
                </button>
              </div>
              <div className="hidden md:flex col-span-2 justify-center py-2 md:py-0">
                <button
                  onClick={() => handleOpenDraftSwot(draft)}
                  className="w-full max-w-[120px] bg-white border border-blue-200 text-blue-600 text-[10px] font-bold uppercase py-1.5 rounded-full shadow-sm hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                >
                  Edit SWOT
                </button>
              </div>
              <div className="hidden md:block col-span-1 pr-3">
                <p className="text-sm text-gray-500 truncate">{draft.prebuilt_email || "No email"}</p>
              </div>
              <div className="hidden md:flex col-span-2 justify-end items-center">
                {isPublishedDraft ? (
                  <span className="inline-flex text-[10px] font-bold uppercase bg-green-100 text-green-700 px-2 py-1 rounded whitespace-nowrap">Ready</span>
                ) : (
                  <button
                    onClick={() => handlePublishPrebuiltSwot(draft.id)}
                    className="inline-flex whitespace-nowrap text-[10px] font-bold uppercase bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-all"
                  >
                    Publish
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
